import {
  currencyName,
  currencySymbol,
  formatDate,
  formatDateTime,
  formatDateTimeSeconds,
  formatDayMonth,
  formatBytes,
  formatDays,
  formatDecimal,
  formatHours,
  formatMoney,
  formatMonthTitle,
  formatPercent,
  monthName,
  weekdayAbbr,
  weekdayName,
  type CurrencyCode,
  type DecimalString,
  type IsoDate,
} from '@tk/shared';
import { useMemo } from 'react';
import { useValuutta } from '../valuutta';
import { en } from './en';
import { fi, type Avain, type Sanasto } from './fi';
import { kieliNyt, useKieli, type Kieli } from './kieli';

export { asetaKieli, useKieli, type Kieli } from './kieli';
export type { Avain } from './fi';

const SANASTOT: Record<Kieli, Sanasto> = { fi, en };

/** Funktioarvon parametrit, merkkijonoarvon tyhjä lista. */
type Parametrit<T> = T extends (...args: infer P) => string ? P : [];

function hae<A extends Avain>(kieli: Kieli, avain: A, args: Parametrit<Sanasto[A]>): string {
  const arvo = SANASTOT[kieli][avain];
  if (typeof arvo === 'function') {
    return (arvo as (...args: unknown[]) => string)(...args);
  }
  return arvo;
}

/**
 * Käännös komponenttien ulkopuolella — esimerkiksi rajapintakerroksessa,
 * jossa ei ole renderöintiä eikä koukkuja. Ei tilaa: lukee kielen suoraan.
 * Komponenteissa käytetään `useKaannos`, jotta näkymä piirtyy uudelleen
 * kielen vaihtuessa.
 */
export function kaanna<A extends Avain>(avain: A, ...args: Parametrit<Sanasto[A]>): string {
  return hae(kieliNyt(), avain, args);
}

/**
 * Kielikohtaiset muotoilijat. Sama kokoelma kuin `@tk/shared`:n
 * `format*`-funktiot, mutta kieli on jo sidottu — kutsupaikan ei tarvitse
 * kuljettaa sitä mukanaan eikä muistaa välittää oikeaa.
 */
export interface Muotoilu {
  /** `"34040.00"` → `"34 040,00 €"`. Valuutta on järjestelmän asetus. */
  raha: (arvo: DecimalString, valuutta?: CurrencyCode) => string;
  /** Luku ilman valuuttaa. */
  desimaali: (arvo: DecimalString) => string;
  /** Minuutit tunneiksi: `240` → `"4,0 h"` */
  tunnit: (minuutit: number, yksikolla?: boolean) => string;
  /** Henkilötyöpäivät: `"45.00"` → `"45 htp"` */
  htp: (paivat: DecimalString, yksikolla?: boolean) => string;
  prosentti: (osuus: number | null, desimaalit?: number) => string;
  pvm: (paiva: IsoDate) => string;
  /** Aikaleima päivänä ja kellonaikana: `"17.8.2026 klo 9.05"` */
  pvmAika: (aikaleima: string) => string;
  /** Sekunnin tarkkuudella: `"24.9.2026 klo 14.02.17"` (salaisuuden lukuhetki). */
  pvmAikaSekunnit: (aikaleima: string) => string;
  /** Tiivis päivä ilman vuotta: `"17.8."` */
  paivaJaKuukausi: (paiva: IsoDate) => string;
  kuukausiNimi: (kuukausi: number) => string;
  kuukausiOtsikko: (vuosi: number, kuukausi: number) => string;
  viikonpaiva: (isoViikonpaiva: number) => string;
  viikonpaivaLyhyt: (isoViikonpaiva: number) => string;
  /** Valuutan merkki nimikkeisiin: `€`, `kr`, `$`. Ks. `currencySymbol`. */
  valuuttaMerkki: (valuutta?: CurrencyCode) => string;
  /** Valuutan nimi valintalistaan: `Ruotsin kruunu`. */
  valuuttaNimi: (valuutta: CurrencyCode) => string;
  /** Tiedoston koko: `12093` → `11,8 kt` */
  tavut: (tavut: number) => string;
}

function muotoilijat(kieli: Kieli, valuutta: CurrencyCode): Muotoilu {
  return {
    raha: (arvo, oma) => formatMoney(arvo, kieli, oma ?? valuutta),
    desimaali: (arvo) => formatDecimal(arvo, kieli),
    tunnit: (minuutit, yksikolla) => formatHours(minuutit, kieli, yksikolla),
    htp: (paivat, yksikolla) => formatDays(paivat, kieli, yksikolla),
    prosentti: (osuus, desimaalit) => formatPercent(osuus, kieli, desimaalit),
    pvm: (paiva) => formatDate(paiva, kieli),
    pvmAika: (aikaleima) => formatDateTime(aikaleima, kieli),
    pvmAikaSekunnit: (aikaleima) => formatDateTimeSeconds(aikaleima, kieli),
    paivaJaKuukausi: (paiva) => formatDayMonth(paiva, kieli),
    kuukausiNimi: (kuukausi) => monthName(kuukausi, kieli),
    kuukausiOtsikko: (vuosi, kuukausi) => formatMonthTitle(vuosi, kuukausi, kieli),
    viikonpaiva: (paiva) => weekdayName(paiva, kieli),
    viikonpaivaLyhyt: (paiva) => weekdayAbbr(paiva, kieli),
    valuuttaMerkki: (oma) => currencySymbol(oma ?? valuutta, kieli),
    valuuttaNimi: (oma) => currencyName(oma, kieli),
    tavut: (maara) => formatBytes(maara, kieli),
  };
}

export interface Kaannos {
  /** Voimassa oleva kieli — tarvitaan esimerkiksi lajitteluun. */
  kieli: Kieli;
  /** Voimassa oleva valuutta järjestelmäasetuksesta. */
  valuutta: CurrencyCode;
  /** Käännös avaimella; parametrit tulevat sanaston funktiosta. */
  t: <A extends Avain>(avain: A, ...args: Parametrit<Sanasto[A]>) => string;
  /** Lukujen ja päiväysten muotoilu samalla kielellä. */
  m: Muotoilu;
}

/**
 * Käännökset ja muotoilu yhdessä koukussa: käytännössä jokainen näkymä
 * tarvitsee molempia, ja kaksi erillistä koukkua olisi kaksi tuontiriviä
 * ja kaksi kutsua joka tiedostossa.
 */
export function useKaannos(): Kaannos {
  const kieli = useKieli();
  const valuutta = useValuutta();
  return useMemo(
    () => ({
      kieli,
      valuutta,
      t: <A extends Avain>(avain: A, ...args: Parametrit<Sanasto[A]>) => hae(kieli, avain, args),
      m: muotoilijat(kieli, valuutta),
    }),
    [kieli, valuutta],
  );
}
